// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/COGS.tsx   (روی فایل قبلی بنویسید)
//
// نسخه‌ی جدید:
//  - دسته‌بندی‌های ثابت جدید: محصول، کرایه موتر داخلی، کرایه موتر خارجی، کمیشن گمرک،
//    بارنامه، تخلیه ویژه، استعلام صحت عامه، حمالی، سایر مصارف
//  - فرم و اندازه‌ی پنجره بر اساس دسته تغییر می‌کند؛ تعداد موتر الزامی؛ فیلدهای موتر/بارنامه/آی نمبر
//    به تعداد موتر ساخته می‌شوند؛ جمع کرایه و معادل دالر خودکار؛ توضیح خودکار + «توضیحات ۲»
//  - رکوردهای قدیمی با دسته‌بندی‌های قبلی بدون هیچ تغییری باقی می‌مانند
//  - لیست ۵۰تایی سمت سرور + گزارش PDF/Excel + ضمیمه (فایل/صدا)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI, warehouseAPI, customersAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import AttachmentField from "../../../components/AttachmentField";
import PersianDatePicker, { todayGregorian, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Edit2 } from "lucide-react";
import TransitCogsBox from "../../tracking/TransitCogsBox";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";
import { fileUrl } from "../../../utils/txnReport";
import { useTxnList, describeFilter, filterToParams } from "./useTxnList";
import TxnReportModal from "./TxnReportModal";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

// دسته‌بندی‌های ثابت جدید
const COGS_TYPES: { key: string; label: string; icon: string; color: string }[] = [
  { key: "product",            label: "محصول",               icon: "📦", color: "#8b5cf6" },
  { key: "fare_domestic",      label: "کرایه موتر داخلی",    icon: "🚚", color: "#3b82f6" },
  { key: "fare_foreign",       label: "کرایه موتر خارجی",    icon: "🚛", color: "#0ea5e9" },
  { key: "customs_commission", label: "کمیشن گمرک",        icon: "🛃", color: "#f59e0b" },
  { key: "waybill",            label: "بارنامه",              icon: "📄", color: "#10b981" },
  { key: "special_unload",     label: "تخلیه ویژه",          icon: "🏗️", color: "#ef4444" },
  { key: "health_inquiry",     label: "استعلام صحت عامه",    icon: "🩺", color: "#14b8a6" },
  { key: "labor",              label: "حمالی",                icon: "💪", color: "#f97316" },
  { key: "other",              label: "سایر مصارف",          icon: "🧾", color: "#64748b" },
];
const typeLabel = (k: string) => COGS_TYPES.find(t => t.key === k)?.label || "";
const isFareType = (k: string) => k === "fare_domestic" || k === "fare_foreign";
const needsTrucks = (k: string) => k === "product" || isFareType(k);

const FARE_FIELDS: { key: string; label: string }[] = [
  { key: "fare_base",  label: "اصل کرایه" },
  { key: "fare_stop",  label: "توقفی" },
  { key: "fare_toll",  label: "تول پلازا / پنج فیصد / دو فیصد" },
  { key: "fare_scale", label: "باسکول" },
  { key: "fare_city",  label: "کرایه شهری" },
  { key: "fare_other", label: "سایر هزینه‌ها" },
];

const num = (v: any) => { const n = parseFloat(String(v ?? "").replace(/,/g, "")); return isFinite(n) ? n : 0; };

const emptyForm = () => ({
  id: 0,
  legacy: false,                // true = رکورد قدیمی با دسته‌بندی‌های قبلی (فقط اگر کاربر بخواهد دسته‌ی قبلی را نگه دارد)
  legacy_name: "",              // نام دسته‌بندی قبلی رکورد قدیمی (فقط برای نمایش)
  cogs_type: "product",
  date: todayGregorian(), recipient_name: "",
  category_id: "", product_category_id: "",
  product_item_id: "", product_item_type: "",
  currency: "USD", amount: "", exchange_rate: "1",
  status: "pending", account_id: "", account_name: "",
  notes: "", notes2: "", attachment: "",
  truck_plate: "", waybill_number: "",
  truck_count: "", truck_numbers: [] as string[], waybill_numbers: [] as string[], inumbers: [] as string[],
  route_from: "", route_to: "",
  fare_base: "", fare_stop: "", fare_toll: "", fare_scale: "", fare_city: "", fare_other: "",
  cogs_title: "",
});
type FormT = ReturnType<typeof emptyForm>;

const resizeArr = (arr: string[], n: number) => { const a = arr.slice(0, n); while (a.length < n) a.push(""); return a; };

export default function COGS() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [showForm, setShowForm] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [showPay, setShowPay]   = useState<any>(null);
  const [showReport, setShowReport] = useState(false);
  const [cats, setCats]         = useState<any[]>([]);
  const [productItems, setProductItems] = useState<any[]>([]);
  const [cashAccs, setCashAccs] = useState<any[]>([]);
  const [filter, setFilter]     = useState<JournalFilterValues>(emptyFilter);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [tempCats, setTempCats] = useState<string[]>([]);
  const [showCatFilter, setShowCatFilter] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState<any>(null);
  const [showBulkSaleModal, setShowBulkSaleModal] = useState(false);
  const [bulkRows, setBulkRows] = useState<any[]>([]);
  const [bulkSaleCustomerId, setBulkSaleCustomerId] = useState("");
  const [bulkSaleLoading, setBulkSaleLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [saleLoading, setSaleLoading] = useState(false);
  const [toast, setToast]       = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "paid">("all");
  const [form, setForm]         = useState<FormT>(emptyForm());
  const [catForm, setCatForm]   = useState({ name: "", parent_id: "", editId: "" });
  const [payForm, setPayForm]   = useState({ account_id: "" });

  const isEdit = form.id > 0;

  const { list, loading, page, setPage, totalPages, totalAll, pendingCount, confirmedCount, totals, usdTotal, reload } = useTxnList({
    fetcher: (p) => journalAPI.listCogs(p),
    respKey: "cogs",
    filter, statusFilter,
    extra: { categories: selectedCats.length ? selectedCats : undefined },
  });

  const loadMeta = useCallback(async () => {
    const [ec, cust, a, pr, sv] = await Promise.all([
      journalAPI.getExpenseCats(),
      customersAPI.list(),
      accountsAPI.getCashAccounts(),
      warehouseAPI.productsList(),
      warehouseAPI.servicesList(),
    ]);
    if (ec.success)   setCats(ec.categories || []);
    if (cust.success) setCustomers(cust.customers || []);
    if (a.success)    setCashAccs(a.accounts || []);
    const items: any[] = [];
    if (pr.success) (pr.products || []).forEach((p: any) => items.push({ value: `prd_${p.id}`, label: `📦 ${p.name}`, type: "product", id: p.id, name: p.name }));
    if (sv.success) (sv.services || []).forEach((s: any) => items.push({ value: `svc_${s.id}`, label: `🔧 ${s.name}`, type: "service", id: s.id, name: s.name }));
    setProductItems(items);
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const load = () => { loadMeta(); reload(); };

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const catOptions = (arr: any[]) => {
    const result: { value: string; label: string }[] = [{ value: "", label: "— بدون دسته‌بندی —" }];
    arr.filter((c: any) => !c.parent_id).forEach((p: any) => {
      result.push({ value: String(p.id), label: p.name });
      arr.filter((c: any) => c.parent_id === p.id).forEach((ch: any) => {
        result.push({ value: String(ch.id), label: `  └ ${ch.name}` });
      });
    });
    return result;
  };

  // ───────── محاسبه‌ی خودکار ─────────
  const fareTotal = (f: FormT) => FARE_FIELDS.reduce((s, x) => s + num((f as any)[x.key]), 0);
  const amountOf = (f: FormT) => (!f.legacy && isFareType(f.cogs_type)) ? Math.round(fareTotal(f) * 100) / 100 : num(f.amount);
  const rateOf = (f: FormT) => (f.currency === "USD" ? 1 : (num(f.exchange_rate) || 1));
  const usdOf = (f: FormT) => amountOf(f) / rateOf(f);

  const itemNameOf = (f: FormT) => productItems.find(i => i.value === f.product_item_id)?.name || "";

  const buildDesc = (f: FormT) => {
    if (f.legacy) return f.notes;
    const parts: string[] = [`بابت ${typeLabel(f.cogs_type)}${f.cogs_title ? ` (${f.cogs_title})` : ""}`];
    if (needsTrucks(f.cogs_type) && num(f.truck_count) > 0) parts.push(`تعداد موتر: ${num(f.truck_count)}`);
    const itemName = itemNameOf(f);
    if (itemName) parts.push(`جنس: ${itemName}`);
    if (isFareType(f.cogs_type)) {
      if (f.route_from) parts.push(`مبدا: ${f.route_from}`);
      if (f.route_to) parts.push(`مقصد: ${f.route_to}`);
      const used = FARE_FIELDS.filter(x => num((f as any)[x.key]) > 0);
      if (used.length) parts.push(used.map(x => `${x.label}: ${fmt(num((f as any)[x.key]))}`).join(" + ") + ` = جمع ${fmt(amountOf(f))} ${f.currency}`);
      else parts.push(`مبلغ به ارز مصرفی: ${fmt(amountOf(f))} ${f.currency}`);
    } else {
      parts.push(`مبلغ به ارز مصرفی: ${fmt(amountOf(f))} ${f.currency}`);
    }
    parts.push(`معادله به دالر: ${fmt(usdOf(f))}`);
    parts.push(`نرخ: ${rateOf(f)}`);
    return parts.join(" - ");
  };

  // ───────── ثبت و ویرایش ─────────
  const buildPayload = (f: FormT, statusValue: "pending" | "paid") => {
    const cat     = cats.find((c: any) => String(c.id) === f.category_id);
    const account = cashAccs.find((a: any) => String(a.id) === f.account_id);
    const selItem = productItems.find(i => i.value === f.product_item_id);
    const base: any = {
      date: f.date, recipient_name: f.recipient_name,
      product_category_id: f.product_category_id ? parseInt(f.product_category_id) : null,
      product_category_name: "",
      product_item_id: selItem ? selItem.id : null,
      product_item_type: selItem ? selItem.type : null,
      currency: f.currency, amount: amountOf(f),
      exchange_rate: rateOf(f),
      status: statusValue,
      account_id: f.account_id ? parseInt(f.account_id) : null,
      account_name: account?.name || f.account_name || "",
      attachment: f.attachment,
    };
    if (f.legacy) {
      return { ...base, category_id: f.category_id ? parseInt(f.category_id) : null, category_name: cat?.name || "", notes: f.notes, truck_plate: f.truck_plate || "", waybill_number: f.waybill_number || "" };
    }
    const n = Math.max(0, Math.floor(num(f.truck_count)));
    const p: any = { ...base, category_id: null, category_name: typeLabel(f.cogs_type), cogs_type: f.cogs_type, notes: buildDesc(f), notes2: f.notes2 || "", cogs_title: f.cogs_title || "", truck_plate: "", waybill_number: "" };
    if (needsTrucks(f.cogs_type)) p.truck_count = n;
    if (f.cogs_type === "product") p.inumbers = resizeArr(f.inumbers, n).map(x => x.trim());
    if (isFareType(f.cogs_type)) {
      p.truck_numbers = resizeArr(f.truck_numbers, n).map(x => x.trim());
      p.waybill_numbers = resizeArr(f.waybill_numbers, n).map(x => x.trim());
      p.route_from = f.route_from; p.route_to = f.route_to;
      FARE_FIELDS.forEach(x => { p[x.key] = num((f as any)[x.key]); });
    }
    return p;
  };

  const validate = (f: FormT, statusValue: "pending" | "paid") => {
    if (!f.recipient_name.trim()) { showToast("نام دریافت‌کننده الزامی است", false); return false; }
    if (!f.legacy && !f.cogs_type) { showToast("یکی از دسته‌بندی‌های جدید را انتخاب کنید", false); return false; }
    if (!f.legacy) {
      if (needsTrucks(f.cogs_type) && !(num(f.truck_count) >= 1)) { showToast("تعداد موتر الزامی است", false); return false; }
      if (f.cogs_type === "other" && !f.cogs_title.trim()) { showToast("عنوان «سایر مصارف» الزامی است", false); return false; }
    }
    if (!(amountOf(f) > 0)) { showToast(isFareType(f.cogs_type) && !f.legacy ? "حداقل یکی از هزینه‌های کرایه را وارد کنید" : "مبلغ الزامی است", false); return false; }
    if (f.currency !== "USD" && !(num(f.exchange_rate) > 0)) { showToast("نرخ تبادله الزامی است", false); return false; }
    if (statusValue === "paid" && !f.account_id) { showToast("حساب پرداختی برای ثبت نهایی الزامی است", false); return false; }
    return true;
  };

  const closeForm = () => { setShowForm(false); setForm(emptyForm()); };
  const openCreate = () => { setForm(emptyForm()); setShowForm(true); };

  const handleCreate = async (statusValue: "pending" | "paid") => {
    if (!validate(form, statusValue)) return;
    const res = await journalAPI.addCogs(buildPayload(form, statusValue));
    if (res.success) {
      showToast(statusValue === "pending" ? `COGS ${res.serial} در انتظار تأیید ذخیره شد` : `COGS ${res.serial} ثبت شد`);
      closeForm(); load();
    } else showToast(res.message || "خطا", false);
  };

  const openEdit = (e: any) => {
    const isNew = !!e.cogs_type;
    setForm({
      ...emptyForm(),
      id: e.id, legacy: false, legacy_name: isNew ? "" : String(e.category_name || "—").replace(/کمیسیون/g, "کمیشن"), cogs_type: isNew ? e.cogs_type : "",
      date: e.date, recipient_name: e.recipient_name || "",
      category_id: e.category_id ? String(e.category_id) : "",
      product_category_id: e.product_category_id ? String(e.product_category_id) : "",
      product_item_id: e.product_item_type === "service" ? `svc_${e.product_item_id}` : e.product_item_id ? `prd_${e.product_item_id}` : "",
      product_item_type: e.product_item_type || "",
      currency: e.currency, amount: String(parseFloat(e.amount)),
      exchange_rate: String(parseFloat(e.exchange_rate) || 1),
      status: e.status, account_id: e.account_id ? String(e.account_id) : "", account_name: e.account_name || "",
      notes: e.notes || "", attachment: e.attachment || "",
      notes2: isNew ? (e.notes2 || "") : [e.notes2 || e.notes || "", e.truck_plate ? `موتر: ${e.truck_plate}` : "", e.waybill_number ? `بارنامه: ${e.waybill_number}` : ""].filter(Boolean).join(" | "),
      truck_plate: e.truck_plate || "", waybill_number: e.waybill_number || "",
      truck_count: e.truck_count != null ? String(e.truck_count) : "",
      truck_numbers: e.truck_numbers_list || [], waybill_numbers: e.waybill_numbers_list || [], inumbers: e.inumbers_list || [],
      route_from: e.route_from || "", route_to: e.route_to || "",
      fare_base: e.fare_base != null ? String(parseFloat(e.fare_base)) : "", fare_stop: e.fare_stop != null ? String(parseFloat(e.fare_stop)) : "",
      fare_toll: e.fare_toll != null ? String(parseFloat(e.fare_toll)) : "", fare_scale: e.fare_scale != null ? String(parseFloat(e.fare_scale)) : "",
      fare_city: e.fare_city != null ? String(parseFloat(e.fare_city)) : "", fare_other: e.fare_other != null ? String(parseFloat(e.fare_other)) : "",
      cogs_title: e.cogs_title || "",
    });
    setShowForm(true);
  };

  const handleEdit = async () => {
    if (!validate(form, form.status === "paid" ? "paid" : "pending")) return;
    const res = await journalAPI.updateCogs({ ...buildPayload(form, form.status === "paid" ? "paid" : "pending"), id: form.id });
    if (res.success) { showToast("COGS ویرایش شد"); closeForm(); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (item: any) => {
    if (!item.account_id) { setShowPay(item); return; }
    if (!window.confirm(`تأیید نهایی COGS ${item.serial}؟\nمبلغ از حساب کم می‌شود.`)) return;
    const res = await journalAPI.confirmCogs(item.id);
    if (res.success) { showToast("COGS تأیید شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن COGS ${serial} به حالت در انتظار؟\nمبلغ به حساب برگردانده می‌شود.`)) return;
    const res = await journalAPI.revertCogs(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleSaleFromCogs = async () => {
    if (!showSaleModal || !saleCustomerId) { showToast("مشتری را انتخاب کنید", false); return; }
    setSaleLoading(true);
    try {
      const res = await journalAPI.addSaleFromCogs({ cogs_id: showSaleModal.id, customer_id: parseInt(saleCustomerId) });
      if (res.success) {
        showToast(`✅ فروش ${res.serial} — ${res.amount} ${res.currency} ثبت شد`);
        setShowSaleModal(null); setSaleCustomerId("");
      } else showToast(res.message || "خطا در ثبت فروش", false);
    } catch { showToast("خطا در اتصال", false); }
    setSaleLoading(false);
  };

  // فروش گروهی روی «همه‌ی رکوردهای مطابق فیلتر» (نه فقط صفحه‌ی جاری)
  const catParams = () => filterToParams(filter, { categories: selectedCats.length ? selectedCats : undefined, status: statusFilter === "all" ? undefined : statusFilter });
  const openBulk = async () => {
    setBulkSaleCustomerId(""); setBulkRows([]); setShowBulkSaleModal(true);
    const res = await journalAPI.reportData({ type: "cogs", ...catParams() });
    if (res.success) setBulkRows(res.rows || []);
  };

  const handleBulkSale = async () => {
    if (!bulkSaleCustomerId) { showToast("مشتری را انتخاب کنید", false); return; }
    if (bulkRows.length === 0) { showToast("هیچ موردی فیلتر نشده", false); return; }
    setBulkSaleLoading(true);
    try {
      const res = await journalAPI.bulkSaleFromCogs({ cogs_ids: bulkRows.map((e: any) => e.id), customer_id: parseInt(bulkSaleCustomerId) });
      if (res.success) {
        showToast(`✅ ${res.count} فروش ثبت شد` + (res.errors?.length ? ` | ${res.errors.length} خطا` : ""));
        setShowBulkSaleModal(false); setBulkSaleCustomerId("");
      } else showToast(res.message || "خطا", false);
    } catch { showToast("خطا در اتصال", false); }
    setBulkSaleLoading(false);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف COGS ${serial}؟`)) return;
    const res = await journalAPI.deleteCogs(id);
    if (res.success) { showToast("حذف شد"); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handlePay = async () => {
    if (!payForm.account_id) { showToast("حساب الزامی است", false); return; }
    const account = cashAccs.find((a: any) => String(a.id) === payForm.account_id);
    const res = await journalAPI.payCogs(showPay.id, parseInt(payForm.account_id), account?.name || "");
    if (res.success) { showToast("پرداخت شد"); setShowPay(null); setPayForm({ account_id: "" }); load(); }
    else showToast(res.message || "خطا", false);
  };

  const handleAddCat = async () => {
    if (!catForm.name) { showToast("نام الزامی است", false); return; }
    const res = await journalAPI.addExpenseCat({ name: catForm.name, parent_id: catForm.parent_id ? parseInt(catForm.parent_id) : null });
    if (res.success) { showToast("اضافه شد"); setCatForm({ name: "", parent_id: "", editId: "" }); const c = await journalAPI.getExpenseCats(); if (c.success) setCats(c.categories || []); }
    else showToast(res.message || "خطا", false);
  };

  const handleUpdateCat = async () => {
    if (!catForm.name || !catForm.editId) return;
    const res = await journalAPI.updateExpenseCat({ id: parseInt(catForm.editId), name: catForm.name, parent_id: catForm.parent_id ? parseInt(catForm.parent_id) : null });
    if (res.success) {
      showToast("ویرایش شد");
      setCatForm({ name: "", parent_id: "", editId: "" });
      const c = await journalAPI.getExpenseCats();
      if (c.success) setCats(c.categories || []);
      reload();
    } else showToast(res.message || "خطا", false);
  };

  const handleDeleteCat = async (id: number) => {
    if (!window.confirm("حذف این دسته‌بندی؟")) return;
    const res = await journalAPI.deleteExpenseCat(id);
    if (res.success) { const c = await journalAPI.getExpenseCats(); if (c.success) setCats(c.categories || []); }
    else showToast(res.message || "خطا", false);
  };

  const accountOptions = cashAccs.map((a: any) => ({ value: String(a.id), label: a.name }));
  const catFilterLabels = selectedCats.map(id => id.startsWith("t:") ? typeLabel(id.slice(2)) : (cats.find((c: any) => String(c.id) === id)?.name || id));
  const filtersLabel = describeFilter(filter, { account: accountOptions, extra: [catFilterLabels.length ? `دسته: ${catFilterLabels.join("، ")}` : ""] });

  // ───────── اجزای فرم ─────────
  const setF = (patch: Partial<FormT>) => setForm(f => ({ ...f, ...patch }));
  const setTruckCount = (v: string) => {
    const n = Math.min(500, Math.max(0, Math.floor(num(v))));
    setForm(f => ({ ...f, truck_count: v === "" ? "" : String(n), truck_numbers: resizeArr(f.truck_numbers, n), waybill_numbers: resizeArr(f.waybill_numbers, n), inumbers: resizeArr(f.inumbers, n) }));
  };
  const setArr = (key: "truck_numbers" | "waybill_numbers" | "inumbers", i: number, v: string) =>
    setForm(f => { const a = f[key].slice(); a[i] = v; return { ...f, [key]: a }; });

  const ltr = { ...inputSt(T), direction: "ltr" as const };
  const nTrucks = Math.max(0, Math.floor(num(form.truck_count)));
  const curType = COGS_TYPES.find(t => t.key === form.cogs_type);
  const accent = form.legacy ? "#8b5cf6" : (curType?.color || "#8b5cf6");
  const wide = !form.legacy && (isFareType(form.cogs_type) || form.cogs_type === "product");
  const usdEq = usdOf(form);
  const desc = buildDesc(form);

  const COLS = "125px 200px 1fr 105px 1.1fr 1.4fr 175px 230px";

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}
      <TransitCogsBox T={T} dark={dark} onChanged={load} />

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در COGS... (شماره سند، نام دریافت‌کننده، دسته‌بندی، موتر، بارنامه)",
          showDateRange: true, showAmountRange: true, showCurrency: true,
          showStatus: true,
          statusOptions: [{ value: "paid", label: "پرداخت شده" }, { value: "pending", label: "در انتظار" }],
          showCategory: false,
          showAccount: true, accountOptions,
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page} totalPages={totalPages} onPageChange={setPage}
        T={T} dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel هزینه اجناس"
      />

      {/* فیلتر دسته‌بندی */}
      <div style={{ marginBottom: 12, borderRadius: 12, border: `1px solid ${dark ? "rgba(139,92,246,0.25)" : "#e9d5ff"}`, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6" }}>🗂 فیلتر دسته‌بندی</span>
            {selectedCats.length > 0 && <span style={{ background: "#8b5cf6", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{selectedCats.length} دسته انتخاب شده</span>}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {selectedCats.length > 0 && <button onClick={() => { setSelectedCats([]); setTempCats([]); }} style={{ padding: "5px 12px", borderRadius: 7, border: "1.5px solid #ef4444", background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit" }}>× پاک کردن</button>}
            <button onClick={() => { setTempCats([...selectedCats]); setShowCatFilter(v => !v); }} style={{ padding: "5px 14px", borderRadius: 7, border: `1.5px solid #8b5cf6`, background: showCatFilter ? "#8b5cf6" : "transparent", color: showCatFilter ? "#fff" : "#8b5cf6", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
              {showCatFilter ? "▲ بستن" : "▼ انتخاب دسته‌بندی"}
            </button>
          </div>
        </div>

        {showCatFilter && (
          <div style={{ padding: 16, background: dark ? "rgba(0,0,0,0.15)" : "#fff", borderTop: `1px solid ${dark ? "rgba(139,92,246,0.15)" : "#e9d5ff"}` }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button onClick={() => setTempCats([...COGS_TYPES.map(t => `t:${t.key}`), ...cats.map((c: any) => String(c.id))])} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>انتخاب همه</button>
              <button onClick={() => setTempCats([])} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>هیچکدام</button>
              <span style={{ fontSize: 11, color: T.subtext, alignSelf: "center" }}>{tempCats.length} تیک زده شده</span>
            </div>

            {[{ title: "دسته‌بندی‌های جدید", items: COGS_TYPES.map(t => ({ id: `t:${t.key}`, name: `${t.icon} ${t.label}`, parent: false })) },
              { title: "دسته‌بندی‌های قبلی (سوابق گذشته)", items: cats.map((c: any) => ({ id: String(c.id), name: String(c.name).replace(/کمیسیون/g, "کمیشن"), parent: !!c.parent_id })) }].map(group => (
              <div key={group.title} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: T.subtext, marginBottom: 6 }}>{group.title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6 }}>
                  {group.items.map(c => {
                    const checked = tempCats.includes(c.id);
                    return (
                      <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 8, cursor: "pointer", border: `1.5px solid ${checked ? "#8b5cf6" : (dark ? "rgba(255,255,255,0.08)" : "#e2e8f0")}`, background: checked ? (dark ? "rgba(139,92,246,0.18)" : "#f5f3ff") : (dark ? "rgba(255,255,255,0.02)" : "#fafafa"), userSelect: "none" as const }} title={c.name}>
                        <input type="checkbox" checked={checked} onChange={() => setTempCats(prev => checked ? prev.filter(id => id !== c.id) : [...prev, c.id])} style={{ accentColor: "#8b5cf6", width: 16, height: 16, cursor: "pointer", flexShrink: 0 }} />
                        <div style={{ flex: 1, overflow: "hidden", fontSize: 12, fontWeight: checked ? 700 : 500, color: checked ? "#8b5cf6" : T.text, whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                          {c.parent ? <span style={{ color: T.subtext }}>↳ </span> : null}{c.name}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "flex-end", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#e9d5ff"}`, paddingTop: 12 }}>
              <button onClick={() => { setSelectedCats([...tempCats]); setShowCatFilter(false); }} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "inherit", boxShadow: "0 4px 14px rgba(139,92,246,0.4)" }}>
                ✓ اعمال فیلتر {tempCats.length > 0 && `(${tempCats.length} دسته)`}
              </button>
            </div>
          </div>
        )}

        {!showCatFilter && selectedCats.length > 0 && (
          <div style={{ padding: "8px 16px", background: dark ? "rgba(0,0,0,0.1)" : "#fff", borderTop: `1px solid ${dark ? "rgba(139,92,246,0.1)" : "#e9d5ff"}`, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {catFilterLabels.map((nm, i) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: dark ? "rgba(139,92,246,0.2)" : "#ede9fe", color: "#8b5cf6", fontWeight: 600, border: "1px solid rgba(139,92,246,0.3)" }}>{nm}</span>)}
          </div>
        )}
      </div>

      {totalAll > 0 && totals.length > 0 && (
        <div style={{ marginBottom: 12, padding: "10px 16px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.06)" : "#faf5ff", border: `1px solid ${dark ? "rgba(139,92,246,0.15)" : "#e9d5ff"}`, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.subtext }}>🧮 مجموع همه‌ی رکوردهای فیلتر شده:</span>
          {totals.map(t => <span key={t.currency} style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6", direction: "ltr" }}>{fmt(t.amount)} {t.currency}</span>)}
          {usdTotal !== null && <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>≈ ${fmt(usdTotal)}</span>}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <StatusTabs value={statusFilter} onChange={setStatusFilter} totalCount={pendingCount + confirmedCount} pendingCount={pendingCount} T={T} dark={dark} confirmedValue="paid" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {totalAll > 0 && canAdd && (
            <button onClick={openBulk} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", borderRadius: 10, border: "1.5px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
              💰 اعمال فروش گروهی ({totalAll} مورد)
            </button>
          )}
          <button onClick={() => setShowCats(true)} style={{ padding: "10px 16px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>دسته‌بندی‌های قبلی</button>
          {canAdd && <button onClick={openCreate} style={addBtnSt("#8b5cf6")}><Plus size={15} /> ثبت COGS</button>}
        </div>
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ maxHeight: "68vh", overflowY: "auto", overflowX: "auto", paddingBottom: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 1240 }}>
            {list.length === 0 && <Empty T={T} text="هیچ هزینه‌ای یافت نشد" />}
            {list.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "10px 16px", borderRadius: 10, position: "sticky", top: 0, zIndex: 2, background: dark ? "#1e293b" : "#f1f5f9", border: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext }}>
                <div>شماره سند</div><div>موتر / بارنامه / آی نمبر</div><div>دریافت‌کننده</div><div>تاریخ</div><div>دسته‌بندی</div><div>شرح</div>
                <div style={{ textAlign: "left" }}>مبلغ</div><div style={{ textAlign: "center" }}>عملیات</div>
              </div>
            )}
            {list.map((e: any) => {
              const isPending = (e.status || "paid") === "pending";
              const isNew = !!e.cogs_type;
              const trucks = (e.truck_numbers_list || []).filter((x: string) => x).join(", ") || e.truck_plate;
              const waybills = (e.waybill_numbers_list || []).filter((x: string) => x).join(", ") || e.waybill_number;
              const inums = (e.inumbers_list || []).filter((x: string) => x).join(", ");
              const chip = (bg: string, c: string, txt: string) => <span style={{ padding: "3px 10px", borderRadius: 7, background: bg, color: c, fontSize: 11, fontWeight: 700, fontFamily: "monospace", wordBreak: "break-all" }}>{txt}</span>;
              return (
                <div key={e.id} style={{ ...rowSt(dark, T), ...(isPending ? pendingRowStyle(dark) : {}), display: "grid", gridTemplateColumns: COLS, gap: 12, alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <span style={badgeSt("#8b5cf6")}>{e.serial}</span>
                    {isPending && <PendingBadge />}
                    {e.attachment && <a href={fileUrl(e.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", textDecoration: "none" }}>📎 ضمیمه</a>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    {isNew && e.truck_count != null && chip("rgba(139,92,246,0.12)", "#8b5cf6", `🚛 ${e.truck_count} موتر`)}
                    {trucks && chip("rgba(59,130,246,0.1)", "#3b82f6", `🚚 ${trucks}`)}
                    {waybills && chip("rgba(14,165,233,0.1)", "#0ea5e9", `📄 ${waybills}`)}
                    {inums && chip("rgba(16,185,129,0.1)", "#10b981", `آی نمبر: ${inums}`)}
                  </div>
                  <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>{e.recipient_name}</div>
                  <div><DualDate date={e.date} fontSize={12} color={T.subtext} /></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    {e.category_name && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: isNew ? "rgba(139,92,246,0.15)" : (dark ? "rgba(255,255,255,0.06)" : "#f1f5f9"), color: isNew ? "#8b5cf6" : T.subtext, fontWeight: isNew ? 700 : 400 }}>{String(e.category_name).replace(/کمیسیون/g, "کمیشن")}{e.cogs_title ? ` — ${e.cogs_title}` : ""}</span>}
                    {e.item_name && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: dark ? "rgba(16,185,129,0.12)" : "#ecfdf5", color: "#059669" }}>{e.item_name}</span>}
                    {e.product_category_name && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: dark ? "rgba(139,92,246,0.15)" : "#f5f3ff", color: "#8b5cf6" }}>{e.product_category_name}</span>}
                  </div>
                  <div style={{ fontSize: 11, color: T.subtext, lineHeight: 1.6, wordBreak: "break-word" }}>
                    <div style={{ fontStyle: "italic" }}>{e.notes || "—"}</div>
                    {e.notes2 && <div style={{ marginTop: 4, color: T.text }}>✍️ {e.notes2}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
                    <span style={{ fontWeight: 800, color: "#8b5cf6", fontSize: 14, direction: "ltr" }}>{fmt(e.amount)} {e.currency}</span>
                    <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" }}>≈ ${fmt(e.usd_amount)}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {isPending && canEdit && <ConfirmIconButton onClick={() => handleConfirm(e)} />}
                    {!isPending && canEdit && <RevertIconButton onClick={() => handleRevert(e.id, e.serial)} />}
                    <button onClick={() => { setShowSaleModal(e); setSaleCustomerId(""); }} title="اعمال فروش" style={{ ...addBtnSt("#3b82f6", true), fontSize: 11, padding: "5px 10px" }}>💰 فروش</button>
                    {canEdit && <button onClick={() => openEdit(e)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                    {canDelete && <button onClick={() => handleDelete(e.id, e.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ───────── فرم ثبت / ویرایش COGS ───────── */}
      {showForm && (
        <Modal title={isEdit ? `ویرایش COGS${form.legacy ? " (دسته‌بندی قبلی)" : ""}` : "ثبت هزینه اجناس / COGS"} onClose={closeForm} dark={dark} T={T} maxWidth={isFareType(form.cogs_type) && !form.legacy ? 1100 : form.cogs_type === "product" && !form.legacy ? 920 : form.legacy ? 820 : 700}>
          <div style={{ width: "100%" }}>

            {/* رکورد قدیمی: مودال جدید باز می‌شود تا کم‌کم اصلاح شود */}
            {isEdit && form.legacy_name && (
              <div style={{ marginBottom: 14, padding: "12px 16px", borderRadius: 12, background: dark ? "rgba(245,158,11,0.1)" : "#fffbeb", border: "1px solid rgba(245,158,11,0.4)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 13, color: T.text, fontWeight: 700 }}>رکورد قدیمی — دسته‌بندی قبلی: <b style={{ color: "#b45309" }}>{form.legacy_name}</b>{form.legacy ? "" : " — یکی از دسته‌بندی‌های جدید را انتخاب کنید."}</div>
                <button type="button" onClick={() => setF({ legacy: !form.legacy, cogs_type: "" })} style={{ padding: "7px 14px", borderRadius: 9, border: "1.5px solid #f59e0b", background: "transparent", color: "#b45309", cursor: "pointer", fontSize: 12, fontWeight: 800, fontFamily: "inherit" }}>{form.legacy ? "انتخاب دسته‌بندی جدید" : "نگه داشتن دسته‌بندی قبلی (بدون تغییر)"}</button>
              </div>
            )}

            {/* انتخاب دسته‌بندی ثابت */}
            {!form.legacy && (
              <div style={{ marginBottom: 18 }}>
                <Lbl T={T}>دسته‌بندی هزینه *</Lbl>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                  {COGS_TYPES.map(t => {
                    const active = form.cogs_type === t.key;
                    return (
                      <button key={t.key} type="button" disabled={isEdit && !form.legacy_name && !active} onClick={() => setF({ cogs_type: t.key })} style={{
                        padding: "11px 8px", borderRadius: 11, cursor: isEdit && !form.legacy_name ? "default" : "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 800,
                        border: `2px solid ${active ? t.color : T.cardBorder}`, background: active ? t.color + "1c" : "transparent", color: active ? t.color : T.subtext,
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: isEdit && !form.legacy_name && !active ? 0.35 : 1,
                      }}><span>{t.icon}</span>{t.label}</button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: wide ? "1fr 1fr 1fr" : "1fr 1fr", gap: 14 }}>
              <div><Lbl T={T}>نام دریافت‌کننده *</Lbl><input value={form.recipient_name} onChange={e => setF({ recipient_name: e.target.value })} placeholder="نام شخص" style={inputSt(T)} /></div>
              <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setF({ date: v })} /></div>

              {form.legacy
                ? <div><Lbl T={T}>دسته‌بندی هزینه</Lbl><SearchableSelect options={catOptions(cats)} value={form.category_id} onChange={v => setF({ category_id: v })} placeholder="انتخاب دسته‌بندی" /></div>
                : null}

              <div>
                <Lbl T={T}>{form.cogs_type === "product" ? "محصول *" : "محصول یا خدمات مربوطه"}</Lbl>
                <SearchableSelect
                  options={[{ value: "", label: "— بدون انتخاب —" }, ...productItems]}
                  value={form.product_item_id}
                  onChange={v => { const item = productItems.find(i => i.value === v); setF({ product_item_id: v, product_item_type: item?.type || "" }); }}
                  placeholder="انتخاب جنس یا خدمت"
                />
              </div>

              {!form.legacy && form.cogs_type === "other" && (
                <div><Lbl T={T}>عنوان *</Lbl><input value={form.cogs_title} onChange={e => setF({ cogs_title: e.target.value })} placeholder="مثلاً: مصرف متفرقه گدام" style={inputSt(T)} /></div>
              )}

              {!form.legacy && needsTrucks(form.cogs_type) && (
                <div><Lbl T={T}>تعداد موتر *</Lbl><input type="number" min={1} max={500} value={form.truck_count} onChange={e => setTruckCount(e.target.value)} placeholder="مثلاً: 3" style={{ ...ltr, borderColor: !form.truck_count ? "#f59e0b" : undefined }} /></div>
              )}
            </div>

            {/* آی نمبر (محصول) */}
            {!form.legacy && form.cogs_type === "product" && nTrucks > 0 && (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, marginBottom: 10 }}>آی نمبر موترها ({nTrucks})</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 10, maxHeight: 240, overflowY: "auto" }}>
                  {Array.from({ length: nTrucks }).map((_, i) => (
                    <div key={i}><Lbl T={T}>آی نمبر {i + 1}</Lbl><input value={form.inumbers[i] || ""} onChange={e => setArr("inumbers", i, e.target.value)} placeholder="اختیاری" style={ltr} /></div>
                  ))}
                </div>
              </div>
            )}

            {/* مسیر + موتر/بارنامه (کرایه) */}
            {!form.legacy && isFareType(form.cogs_type) && (
              <>
                {nTrucks > 0 && (
                  <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text, marginBottom: 10 }}>شماره موتر و شماره بارنامه ({nTrucks} موتر)</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxHeight: 260, overflowY: "auto" }}>
                      {Array.from({ length: nTrucks }).map((_, i) => (
                        <div key={i} style={{ display: "contents" }}>
                          <div><Lbl T={T}>🚚 شماره موتر {i + 1}</Lbl><input value={form.truck_numbers[i] || ""} onChange={e => setArr("truck_numbers", i, e.target.value)} placeholder="اختیاری" style={ltr} /></div>
                          <div><Lbl T={T}>📄 شماره بارنامه {i + 1}</Lbl><input value={form.waybill_numbers[i] || ""} onChange={e => setArr("waybill_numbers", i, e.target.value)} placeholder="اختیاری" style={ltr} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
                  <div><Lbl T={T}>مبدا</Lbl><input value={form.route_from} onChange={e => setF({ route_from: e.target.value })} placeholder="شهر / مرز مبدا" style={inputSt(T)} /></div>
                  <div><Lbl T={T}>مقصد</Lbl><input value={form.route_to} onChange={e => setF({ route_to: e.target.value })} placeholder="شهر / مرز مقصد" style={inputSt(T)} /></div>
                </div>
              </>
            )}

            {/* ارز و نرخ */}
            <div style={{ display: "grid", gridTemplateColumns: wide ? "1fr 1fr 1fr" : "1fr 1fr", gap: 14, marginTop: 16 }}>
              <div><Lbl T={T}>ارز مصرفی</Lbl><SearchableSelect options={CURRENCIES.map(c => ({ value: c, label: c }))} value={form.currency} onChange={v => setF({ currency: v, exchange_rate: v === "USD" ? "1" : (form.exchange_rate === "1" ? "" : form.exchange_rate) })} placeholder="ارز" /></div>
              <div>
                <Lbl T={T}>نرخ تبادله به دالر {form.currency === "USD" && <span style={{ color: "#94a3b8", fontSize: 10 }}>(دالر = ۱)</span>}</Lbl>
                <input type="number" value={form.currency === "USD" ? "1" : form.exchange_rate} disabled={form.currency === "USD"} onChange={e => setF({ exchange_rate: e.target.value })} placeholder="مثلاً: 70" style={{ ...ltr, opacity: form.currency === "USD" ? 0.6 : 1 }} />
              </div>
            </div>

            {/* هزینه‌های کرایه */}
            {!form.legacy && isFareType(form.cogs_type) ? (
              <div style={{ marginTop: 16, padding: 14, borderRadius: 12, border: `1.5px solid ${accent}55`, background: dark ? accent + "12" : accent + "0d" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginBottom: 10 }}>هزینه‌های کرایه ({form.currency}) — جمع به‌صورت خودکار محاسبه می‌شود</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                  {FARE_FIELDS.map(x => (
                    <div key={x.key}><Lbl T={T}>{x.label}</Lbl><input type="text" inputMode="decimal" value={(form as any)[x.key]} onChange={e => setF({ [x.key]: e.target.value } as any)} placeholder="0" style={ltr} /></div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14, marginTop: 16 }}>
                <div><Lbl T={T}>مبلغ *</Lbl><input type="text" inputMode="decimal" value={form.amount} onChange={e => setF({ amount: e.target.value })} placeholder="0.00" style={ltr} /></div>
              </div>
            )}

            {/* جمع و معادل دالر */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
              <div>
                <Lbl T={T}>{!form.legacy && isFareType(form.cogs_type) ? "جمع کل (به ارز مصرفی)" : "مبلغ به ارز مصرفی"}</Lbl>
                <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.1)" : "#f5f3ff", color: "#8b5cf6", fontWeight: 800, fontSize: 15, direction: "ltr", textAlign: "right" }}>{fmt(amountOf(form))} {form.currency}</div>
              </div>
              <div>
                <Lbl T={T}>معادل به دالر</Lbl>
                <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(16,185,129,0.1)" : "#ecfdf5", color: "#10b981", fontWeight: 800, fontSize: 15, direction: "ltr", textAlign: "right" }}>${fmt(usdEq)}</div>
                <div style={{ fontSize: 11, color: T.subtext, marginTop: 4 }}>{fmt(amountOf(form))} ÷ {rateOf(form)}</div>
              </div>
            </div>

            {/* حساب و وضعیت */}
            <div style={{ display: "grid", gridTemplateColumns: isEdit ? "1fr 1fr" : "1fr", gap: 14, marginTop: 16 }}>
              {isEdit && (
                <div><Lbl T={T}>وضعیت پرداخت</Lbl><SearchableSelect options={[{ value: "pending", label: "در انتظار" }, { value: "paid", label: "پرداخت شده" }]} value={form.status} onChange={v => setF({ status: v, account_id: "", account_name: "" })} placeholder="وضعیت" /></div>
              )}
              {(!isEdit || form.status === "paid") && (
                <div>
                  <Lbl T={T}>حساب پرداختی {isEdit ? "*" : <span style={{ color: "#94a3b8", fontSize: 10 }}>(برای ثبت نهایی الزامی)</span>}</Lbl>
                  <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={form.account_id}
                    onChange={v => { const ac = cashAccs.find((x: any) => String(x.id) === v); setF({ account_id: v, account_name: ac?.name || "" }); }} placeholder="انتخاب حساب" />
                </div>
              )}
            </div>

            {/* توضیحات */}
            {form.legacy ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 16 }}>
                <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setF({ notes: e.target.value })} placeholder="توضیحات..." style={inputSt(T)} /></div>
                <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={form.truck_plate} onChange={e => setF({ truck_plate: e.target.value })} placeholder="مثلاً: 12345" style={ltr} /></div>
                <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={form.waybill_number} onChange={e => setF({ waybill_number: e.target.value })} placeholder="شماره بارنامه..." style={ltr} /></div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
                <div>
                  <Lbl T={T}>توضیحات (خودکار)</Lbl>
                  <div style={{ padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#f1f5f9", border: `1.5px dashed ${T.border}`, color: T.text, fontSize: 12, lineHeight: 1.9, minHeight: 66, wordBreak: "break-word" }}>{desc}</div>
                </div>
                <div>
                  <Lbl T={T}>توضیحات ۲ (اختیاری — خودتان بنویسید)</Lbl>
                  <textarea value={form.notes2} onChange={e => setF({ notes2: e.target.value })} placeholder="توضیح بیشتر..." rows={3} style={{ ...inputSt(T), minHeight: 66, resize: "vertical" }} />
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl>
              <AttachmentField value={form.attachment} fileName={form.attachment ? String(form.attachment).split("/").pop() : ""} onChange={(path) => setF({ attachment: path })} uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark} />
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`, flexWrap: "wrap" }}>
              {isEdit ? <span /> : <PendingHint T={T} />}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <BtnCancel onClick={closeForm} T={T} />
                {isEdit ? (
                  <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#8b5cf6" label="ذخیره ویرایش" />
                ) : (
                  <>
                    <PendingSubmitButton onClick={() => submit(() => handleCreate("pending"))} loading={submitting} label="ذخیره COGS در انتظار" />
                    <ConfirmSubmitButton onClick={() => submit(() => handleCreate("paid"))} loading={submitting} color="#8b5cf6" label="ثبت COGS" />
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* مودال پرداخت */}
      {showPay && (
        <Modal title={`پرداخت COGS: ${showPay.serial}`} onClose={() => setShowPay(null)} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "14px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff" }}>
            <span style={{ fontSize: 13, color: T.text }}>دریافت‌کننده: <strong>{showPay.recipient_name}</strong> | مبلغ: <strong style={{ color: "#8b5cf6" }}>{fmt(showPay.amount)} {showPay.currency}</strong></span>
          </div>
          <Lbl T={T}>حساب پرداختی *</Lbl>
          <SearchableSelect options={cashAccs.map((a: any) => ({ value: String(a.id), label: `${a.code} — ${a.name} (${a.currency}) | موجودی: ${fmt(a.balance)}` }))} value={payForm.account_id} onChange={v => setPayForm({ account_id: v })} placeholder="انتخاب حساب" />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => setShowPay(null)} T={T} />
            <BtnSave onClick={() => submit(handlePay)} loading={submitting} color="#10b981" label="تایید پرداخت" />
          </div>
        </Modal>
      )}

      {/* مودال اعمال فروش گروهی */}
      {showBulkSaleModal && (
        <Modal title={`اعمال فروش گروهی — ${bulkRows.length} مورد`} onClose={() => { setShowBulkSaleModal(false); setBulkSaleCustomerId(""); }} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff", border: "1px solid rgba(59,130,246,0.25)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6", marginBottom: 8 }}>📋 موارد انتخاب شده ({bulkRows.length} مورد):</div>
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {bulkRows.map((e: any) => (
                <div key={e.id} style={{ display: "flex", gap: 10, fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}` }}>
                  <span style={{ color: "#8b5cf6", fontWeight: 700, minWidth: 110, flexShrink: 0 }}>{e.serial}</span>
                  <span style={{ color: T.text, flex: 1 }}>{e.recipient_name}</span>
                  <span style={{ color: "#8b5cf6", fontWeight: 700 }}>{fmt(e.amount)} {e.currency}</span>
                  <span style={{ color: "#10b981", fontSize: 11 }}>${fmt(e.usd_amount)}</span>
                  <span style={{ color: T.subtext, fontSize: 11 }}>{e.date}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16, padding: "8px 14px", borderRadius: 8, background: dark ? "rgba(16,185,129,0.06)" : "#f0fdf4", border: "1px solid rgba(16,185,129,0.2)", fontSize: 12, color: T.subtext }}>
            ✅ هر مورد به صورت یک فروش جداگانه با همان تاریخ، مبلغ و جزییات در لیست فروشات مشتری ثبت می‌شود.
          </div>
          <Lbl T={T}>انتخاب مشتری *</Lbl>
          <SearchableSelect
            options={customers.map((c: any) => ({
              value: String(c.id),
              label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` — ${c.currency || "USD"}`,
            }))}
            value={bulkSaleCustomerId}
            onChange={v => setBulkSaleCustomerId(v)}
            placeholder="— انتخاب مشتری"
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => { setShowBulkSaleModal(false); setBulkSaleCustomerId(""); }} T={T} />
            <button
              onClick={handleBulkSale}
              disabled={bulkSaleLoading || !bulkSaleCustomerId}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: bulkSaleLoading || !bulkSaleCustomerId ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: bulkSaleLoading || !bulkSaleCustomerId ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}
            >
              {bulkSaleLoading ? "در حال ثبت..." : `💰 ثبت ${bulkRows.length} فروش`}
            </button>
          </div>
        </Modal>
      )}

      {/* مودال اعمال فروش از COGS */}
      {showSaleModal && (
        <Modal title="اعمال فروش از COGS" onClose={() => { setShowSaleModal(null); setSaleCustomerId(""); }} dark={dark} T={T}>
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: dark ? "rgba(139,92,246,0.08)" : "#faf5ff", border: "1px solid rgba(139,92,246,0.3)" }}>
            <div style={{ fontSize: 12, color: T.subtext, marginBottom: 6 }}>مشخصات هزینه COGS:</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
              <span><strong style={{ color: "#8b5cf6" }}>{showSaleModal.serial}</strong></span>
              <span style={{ color: T.text }}>{showSaleModal.recipient_name}</span>
              <span style={{ fontWeight: 800, color: "#8b5cf6" }}>{fmt(showSaleModal.amount)} {showSaleModal.currency}</span>
              {showSaleModal.currency !== "USD" && <span style={{ color: "#10b981" }}>≈ ${fmt(showSaleModal.usd_amount)}</span>}
              <DualDate date={showSaleModal.date} fontSize={12} color={T.subtext} />
            </div>
          </div>
          <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff", border: "1px solid rgba(59,130,246,0.2)", fontSize: 12, color: T.subtext }}>
            ℹ️ اگر ارز مشتری با ارز هزینه یکسان باشد: همان مبلغ فروخته می‌شود.<br/>
            اگر ارز مشتری دالر (USD) باشد: معادل دالری (<strong style={{ color: "#10b981" }}>${fmt(showSaleModal.usd_amount)}</strong>) فروخته می‌شود.
          </div>
          <Lbl T={T}>انتخاب مشتری *</Lbl>
          <SearchableSelect
            options={customers.map((c: any) => ({
              value: String(c.id),
              label: `${c.first_name || ""} ${c.last_name || ""} ${c.company_name || ""}`.trim() + ` — ${c.currency || "USD"}`,
            }))}
            value={saleCustomerId}
            onChange={v => setSaleCustomerId(v)}
            placeholder="— انتخاب مشتری"
          />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
            <BtnCancel onClick={() => { setShowSaleModal(null); setSaleCustomerId(""); }} T={T} />
            <button
              onClick={handleSaleFromCogs}
              disabled={saleLoading || !saleCustomerId}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saleLoading || !saleCustomerId ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: saleLoading || !saleCustomerId ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}
            >
              {saleLoading ? "در حال ثبت..." : "💰 ثبت فروش"}
            </button>
          </div>
        </Modal>
      )}

      {/* مودال دسته‌بندی هزینه */}
      {showCats && (
        <Modal title="مدیریت دسته‌بندی هزینه COGS" onClose={() => setShowCats(false)} dark={dark} T={T}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16, padding: 16, borderRadius: 12, background: dark ? "rgba(255,255,255,0.03)" : "#f8fafc", border: `1px solid ${T.cardBorder}` }}>
            <div>
              <Lbl T={T}>{catForm.editId ? "ویرایش نام" : "نام دسته‌بندی"} *</Lbl>
              <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="نام" style={inputSt(T)} />
            </div>
            <div>
              <Lbl T={T}>{catForm.editId ? "تغییر دسته مادر" : "زیرمجموعه‌ی"}</Lbl>
              <SearchableSelect
                options={[{ value: "", label: "— دسته اصلی —" }, ...cats.filter((c: any) => !c.parent_id && String(c.id) !== catForm.editId).map((c: any) => ({ value: String(c.id), label: c.name }))]}
                value={catForm.parent_id}
                onChange={v => setCatForm(f => ({ ...f, parent_id: v }))}
                placeholder="دسته مادر"
              />
            </div>
            <div style={{ gridColumn: "1/-1", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {catForm.editId && <button onClick={() => setCatForm({ name: "", parent_id: "", editId: "" })} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 12, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>انصراف</button>}
              <button onClick={catForm.editId ? handleUpdateCat : handleAddCat} style={addBtnSt("#8b5cf6", true)}>
                {catForm.editId ? "ذخیره" : <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Plus size={13} /> افزودن</span>}
              </button>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {cats.filter((c: any) => !c.parent_id).map((parent: any) => (
              <div key={parent.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", border: `1px solid ${T.cardBorder}` }}>
                  <span style={{ fontWeight: 700, color: T.text, flex: 1, fontSize: 13 }}>{parent.name}</span>
                  {canEdit && <button onClick={() => setCatForm({ name: parent.name, parent_id: "", editId: String(parent.id) })} style={iconBtnSt("#3b82f6")}><Edit2 size={12} /></button>}
                  {canDelete && <button onClick={() => handleDeleteCat(parent.id)} style={iconBtnSt("#ef4444")}><Trash2 size={12} /></button>}
                </div>
                {cats.filter((c: any) => c.parent_id === parent.id).map((child: any) => (
                  <div key={child.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px 8px 32px", borderRadius: 8, marginTop: 4, background: dark ? "rgba(255,255,255,0.02)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                    <span style={{ fontSize: 11, color: T.subtext }}>└</span>
                    <span style={{ color: T.text, flex: 1, fontSize: 13 }}>{child.name}</span>
                    {canEdit && <button onClick={() => setCatForm({ name: child.name, parent_id: String(child.parent_id), editId: String(child.id) })} style={iconBtnSt("#3b82f6")}><Edit2 size={12} /></button>}
                    {canDelete && <button onClick={() => handleDeleteCat(child.id)} style={iconBtnSt("#ef4444")}><Trash2 size={12} /></button>}
                  </div>
                ))}
              </div>
            ))}
            {cats.length === 0 && <Empty T={T} text="هیچ دسته‌بندی ایجاد نشده" />}
          </div>
        </Modal>
      )}
      {showReport && (
        <TxnReportModal type="cogs" title="هزینه اجناس (COGS)" filter={filter} filtersLabel={filtersLabel}
          extraParams={{ categories: selectedCats.length ? selectedCats : undefined }} itemOptions={productItems} categoryGroups={[{ title: "دسته‌بندی‌های جدید", items: COGS_TYPES.map(t => ({ id: `t:${t.key}`, name: `${t.icon} ${t.label}` })) }, { title: "دسته‌بندی‌های قبلی (سوابق گذشته)", items: cats.map((c: any) => ({ id: String(c.id), name: String(c.name).replace(/کمیسیون/g, "کمیشن") })) }]} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}